/*
 * Cart plan unifier — Your Farmer
 *
 * Rules:
 *  - Add-to-cart: if the cart has any subscription plan active, every line (including
 *    the freshly-added one-time item) is cascaded onto that plan. Target = last
 *    subscription line's plan (last-added-wins). Products that can't accept the plan
 *    are left alone.
 *  - Any dropdown change (Appstle cart widget or our drawer <select>) cascades that
 *    value to every line — including "One-time purchase" (empty value), which drops
 *    the plan on every line.
 *
 * Exposes window.YFCartPlan = { setAllLinesTo, setLineTo }.
 */
(function () {
  'use strict';
  if (window.__yfPlanUnifierReady) return;
  window.__yfPlanUnifierReady = true;

  // NOTE: previously we wrapped window.fetch to transparently retry cart writes on 429.
  // In practice the retry storm kept the rate-limit bucket empty and amplified the
  // problem, especially combined with background pollers (Klaviyo reviews widget hits
  // /cart.js on an interval). One failure is recoverable via a user retry; compounding
  // failures grinds the page to a halt. Leaving the wrapper off is strictly better.

  // Set window.YF_PLAN_DEBUG = false (or remove ?yfdebug=1) to silence logs.
  var DEBUG = (function () {
    if (window.YF_PLAN_DEBUG === false) return false;
    if (window.YF_PLAN_DEBUG === true) return true;
    try { return /[?&]yfdebug=1/.test(location.search) || location.hostname.indexOf('myshopify.com') !== -1; } catch (e) { return false; }
  })();
  var LOG = DEBUG
    ? function () { try { console.log.apply(console, ['[YFPLAN]'].concat([].slice.call(arguments))); } catch (e) {} }
    : function () {};

  // Native fetch via hidden iframe — bypasses Appstle/Rebuy fetch wrappers.
  function nativeFetch() {
    if (window.__yfNativeFetch) return window.__yfNativeFetch;
    try {
      var iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
      (document.body || document.documentElement).appendChild(iframe);
      window.__yfNativeFetch = iframe.contentWindow.fetch.bind(window);
      return window.__yfNativeFetch;
    } catch (e) {
      return window.fetch.bind(window);
    }
  }

  var CART_URL = (window.theme && theme.routes && theme.routes.cart_url) || '/cart.js';
  var CART_CHANGE_URL = (window.theme && theme.routes && theme.routes.cart_change_url) || '/cart/change.js';

  var cascading = false;

  // Toast spinner — gives the shopper visible feedback that the cart is being updated.
  // Visual comes from assets/yf-spinner.css (loaded in <head>) so we stay in sync with
  // the page-transition loading-bar.
  var SPINNER_ID = 'yf-plan-spinner';
  // Tracks whether Appstle's own loader is currently active (set by the bridge
  // below). When true, hideSpinner() leaves the cow in place so our cascade
  // ending first doesn't blank the feedback mid-Appstle-call.
  var appstleActive = false;

  // Rotating verb list for the add-to-cart spinner — swaps every 1s. Chosen
  // to feel farm-y: the animals are "thinking" about your order.
  var ROTATE_VERBS = ['Ruminating', 'Mustering', 'Gathering', 'Herding', 'Pondering', 'Mulling', 'Chewing'];
  var rotateTimer = null;
  var lastVerb = '';

  function stopRotate() {
    if (rotateTimer) { clearInterval(rotateTimer); rotateTimer = null; }
  }

  function pickVerb() {
    // Avoid two of the same verb back-to-back.
    var v;
    do { v = ROTATE_VERBS[Math.floor(Math.random() * ROTATE_VERBS.length)]; }
    while (v === lastVerb && ROTATE_VERBS.length > 1);
    lastVerb = v;
    return v;
  }

  // msg: string → static text.  opts.rotate: true → cycle ROTATE_VERBS every 1s.
  function showSpinner(msg, opts) {
    var bd = document.getElementById(SPINNER_ID);
    if (!bd) {
      bd = document.createElement('div');
      bd.id = SPINNER_ID;
      bd.className = 'yf-spinner-backdrop';
      bd.setAttribute('role', 'status');
      bd.setAttribute('aria-live', 'polite');
      bd.innerHTML =
        '<div class="yf-spinner-toast">' +
          '<div class="yf-spinner-parade" aria-hidden="true">' +
            '<span class="yf-spinner-animal yf-spinner-animal--farmer"><span class="yf-spinner-animal__glyph">\uD83E\uDDD1\u200D\uD83C\uDF3E</span></span>' +
            '<span class="yf-spinner-animal yf-spinner-animal--sheep"><span class="yf-spinner-animal__glyph">\uD83D\uDC11</span></span>' +
            '<span class="yf-spinner-animal yf-spinner-animal--rooster"><span class="yf-spinner-animal__glyph">\uD83D\uDC13</span></span>' +
            '<span class="yf-spinner-animal yf-spinner-animal--cow"><span class="yf-spinner-animal__glyph">\uD83D\uDC04</span></span>' +
          '</div>' +
          '<span class="yf-spinner-text"></span>' +
        '</div>';
      document.body.appendChild(bd);
    }
    var textEl = bd.querySelector('.yf-spinner-text');
    stopRotate();
    if (opts && opts.rotate) {
      var setRotating = function () { textEl.textContent = pickVerb() + '\u2026'; };
      setRotating();
      rotateTimer = setInterval(setRotating, 1000);
    } else {
      textEl.textContent = msg || 'Updating your cart\u2026';
    }
  }
  function hideSpinner() {
    if (appstleActive) return;
    stopRotate();
    var bd = document.getElementById(SPINNER_ID);
    if (bd && bd.parentNode) bd.parentNode.removeChild(bd);
  }

  // Appstle bridge — show the cow whenever Appstle would have shown its own
  // grey-ring loader. The signal lives on #appstle_overlay (the full-page dim
  // backdrop); the .appstle_loader ring inside it is always visible relative
  // to the overlay, so the overlay's display toggle is what gates everything.
  //
  // Defences against the earlier stuck-cow bug:
  //   - only fire on mutation *changes*, not the initial DOM state
  //   - "active" requires an explicit visible inline display value (block,
  //     flex, etc.) — empty or 'none' = inactive
  //   - 8s safety timeout auto-releases appstleActive if a hide mutation is
  //     missed
  //
  // yf-spinner.css also gives every .yf-spinner-backdrop pointer-events:none,
  // so even a stuck cow can never freeze page clicks.
  var APPSTLE_SELECTOR = '#appstle_overlay, .appstle_loader, .appstle_loader_wrapper, .appstle-loader';
  var APPSTLE_SAFETY_MS = 8000;
  var appstleSafetyTimer = null;
  function isExplicitlyVisibleInline(el) {
    if (!el || !el.isConnected || el.hidden) return false;
    var d = el.style && el.style.display;
    // '' = no inline display set (default/hidden by Appstle's stylesheet).
    // 'none' = explicitly hidden. Anything else = Appstle deliberately showing it.
    return d !== '' && d !== 'none';
  }
  function recomputeAppstleActive() {
    var loaders = document.querySelectorAll(APPSTLE_SELECTOR);
    var active = false;
    for (var i = 0; i < loaders.length; i++) {
      if (isExplicitlyVisibleInline(loaders[i])) { active = true; break; }
    }
    if (active === appstleActive) return;
    appstleActive = active;
    clearTimeout(appstleSafetyTimer);
    if (active) {
      LOG('appstle-bridge: loader shown → cow up');
      showSpinner('Updating subscription…');
      // Self-heal: if we miss the hide mutation, force-release after 8s.
      appstleSafetyTimer = setTimeout(function () {
        LOG('appstle-bridge: safety timeout — forcing cow down');
        appstleActive = false;
        if (!cascading) {
          var bd = document.getElementById(SPINNER_ID);
          if (bd && bd.parentNode) bd.parentNode.removeChild(bd);
        }
      }, APPSTLE_SAFETY_MS);
    } else {
      LOG('appstle-bridge: loader hidden → cow down (unless cascade)');
      if (!cascading) {
        var bd = document.getElementById(SPINNER_ID);
        if (bd && bd.parentNode) bd.parentNode.removeChild(bd);
      }
    }
  }
  function startAppstleBridge() {
    if (!document.body) {
      document.addEventListener('DOMContentLoaded', startAppstleBridge, { once: true });
      return;
    }
    var obs = new MutationObserver(recomputeAppstleActive);
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden']
    });
    // Deliberately do NOT call recomputeAppstleActive() here. Initial state of
    // .appstle_loader elements is "inactive by default" — we only react to
    // Appstle mutating them to a visible inline style after page load.
  }
  startAppstleBridge();

  // Quick-view modal Appstle bridge. The theme's quickview() loads a product's
  // ?view=modal HTML into a drawer. The Appstle widget on the resulting form
  // is normally re-initialised by an <img onload="pollAppstleInit()"> trick in
  // buy-buttons.liquid — but if that image fails to load (DNS, CDN hiccup,
  // adblock), the widget never appears in the modal. Hook the theme's
  // quickview:loaded event and call pollAppstleInit directly as a backup.
  // Retries cover the case where Appstle's main script hasn't finished
  // booting at the moment the modal first opens.
  function bridgeQuickviewToAppstle() {
    var logged = false;
    var triggerInit = function () {
      try {
        if (typeof window.pollAppstleInit === 'function') {
          window.pollAppstleInit();
          // Log once per modal open, not once per retry — pollAppstleInit
          // is fired at 50/250/800ms to cover the case where Appstle's own
          // script hasn't finished booting on the first call.
          if (!logged) {
            LOG('quickview bridge: called pollAppstleInit');
            logged = true;
          }
        }
      } catch (e) { LOG('quickview bridge error', e); }
    };
    [50, 250, 800].forEach(function (d) { setTimeout(triggerInit, d); });
  }
  document.addEventListener('quickview:loaded', bridgeQuickviewToAppstle);

  function fetchCart() {
    return nativeFetch()(CART_URL + '?_=' + Date.now(), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  // Collect the live cart-containing section IDs from the DOM (main-cart on the cart page,
  // cart-drawer elsewhere, etc.) so a subsequent /cart/update.js call can return rendered
  // HTML for each one — lets cart-items swap innerHTML in place instead of page-reloading.
  function collectCartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items[data-section-id], main-cart[data-section-id], cart-drawer[data-section-id]').forEach(function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  // POST /cart/update.js with empty updates + the sections list. Response is { cart, sections }.
  // We fold sections into the cart payload as .sections so theme cart-items can re-render.
  function fetchCartWithSections() {
    var ids = collectCartSectionIds();
    if (!ids.length) return fetchCart();
    var body = JSON.stringify({ updates: {}, sections: ids.join(',') });
    return nativeFetch()((window.theme && theme.routes && theme.routes.cart_update_url) || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body
    }).then(function (r) { return r.json(); });
  }

  function changeLineOnce(lineKey, sellingPlan) {
    var body = { id: lineKey, selling_plan: sellingPlan || '' };
    return nativeFetch()(CART_CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (j) {
          var err = new Error('cart/change ' + r.status + ' ' + (j && (j.description || j.message) || ''));
          err.status = r.status; err.body = j;
          throw err;
        }, function () {
          var err = new Error('cart/change ' + r.status);
          err.status = r.status;
          throw err;
        });
      }
      return r.json();
    });
  }

  // Retry on 409 only — cart version conflict from concurrent writers (usually Appstle).
  // 429s are treated as hard failures: retrying while rate-limited just prolongs the
  // outage. Caller's outer catch will log and skip the line.
  function changeLine(lineKey, sellingPlan, attempt) {
    attempt = attempt || 0;
    return changeLineOnce(lineKey, sellingPlan).catch(function (e) {
      if (e && e.status === 409 && attempt < 3) {
        var delay = 200 + 150 * attempt;
        return new Promise(function (res) { setTimeout(res, delay); })
          .then(function () { return changeLine(lineKey, sellingPlan, attempt + 1); });
      }
      throw e;
    });
  }

  function planIdOf(item) {
    if (item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      return String(item.selling_plan_allocation.selling_plan.id);
    }
    return null;
  }

  // Per-line tag locks. Two sources, evaluated in order:
  //   1. Hidden line-item properties on the line itself
  //      (properties._yf_one_time_only / properties._yf_sub_only).
  //      Set by buy-buttons.liquid hidden inputs at /cart/add time, so the
  //      lock travels with the line in /cart.js. Self-identifying — works
  //      immediately, no server round-trip needed.
  //   2. Server-rendered fallback JSON for older lines that pre-date the
  //      properties (no _yf_* property set):
  //        <script type="application/json" id="yf-cart-line-locks">
  //          { "<line.key>": { "subOnly": bool, "oneTimeOnly": bool } }
  //      Built from cart.items[].product.tags in main-cart.liquid /
  //      cart-drawer.liquid. Necessarily stale right after a fresh add.
  function getLineLocks() {
    var el = document.getElementById('yf-cart-line-locks');
    if (!el) return {};
    try { return JSON.parse(el.textContent || '{}') || {}; } catch (e) { return {}; }
  }
  // Pulls the lock for `item` (preferred) or falls back to a key lookup for
  // call sites that don't have the item handy. Properties win — they're the
  // line's own statement about what it should be.
  function lockForItem(item, locks) {
    if (item && item.properties) {
      var p = item.properties;
      var hasProp = ('_yf_one_time_only' in p) || ('_yf_sub_only' in p);
      if (hasProp) {
        return {
          subOnly: !!p._yf_sub_only,
          oneTimeOnly: !!p._yf_one_time_only
        };
      }
    }
    if (item && locks && locks[item.key]) return locks[item.key];
    return { subOnly: false, oneTimeOnly: false };
  }
  function lockForKey(locks, key) {
    return (locks && locks[key]) || { subOnly: false, oneTimeOnly: false };
  }
  // True if cascading `target` to a line with the given lock would violate it.
  // - target = "" (one-time): subOnly lines must keep their plan → skip.
  // - target = "<plan>"     : oneTimeOnly lines must stay one-time → skip.
  function lockForbids(lock, target) {
    if (!lock) return false;
    if (target) return !!lock.oneTimeOnly;
    return !!lock.subOnly;
  }

  // True iff the line was added with the explicit-one-time marker AND is
  // currently still un-subscribed. The "currently un-subscribed" check makes
  // the marker a one-shot: once the picker forces a marked line onto a plan,
  // the marker becomes irrelevant and the line is treated like any other sub
  // line in subsequent cascades. Avoids stale markers creating mixed-sub-plan
  // states.
  //
  // Distinct from the tag-based lock (lockForItem above): this marker is set
  // by the shopper deliberately picking the One-time radio on a PDP that has
  // a plan picker. The tag-based lock is set by buy-buttons.liquid for any
  // product tagged `one-time-only` or `subscription-only`. Both should skip
  // cascade — checked together throughout the file.
  function isExplicitOneTime(item) {
    if (!item) return false;
    if (item.selling_plan_allocation) return false;
    return !!(item.properties && item.properties._yf_explicit_onetime);
  }

  // Find the plan on the line matching a specific variant ID (the just-added variant).
  // Don't rely on cart.items ordering — Shopify's order is unreliable across add/update.
  // If multiple lines match the variant, prefer one with a selling plan.
  function planForVariant(cart, variantId) {
    if (!cart || !Array.isArray(cart.items) || !variantId) return null;
    var vid = String(variantId);
    var matches = cart.items.filter(function (it) { return String(it.variant_id) === vid; });
    if (!matches.length) return null;
    for (var i = 0; i < matches.length; i++) {
      var p = planIdOf(matches[i]);
      if (p) return p;
    }
    return null; // variant found but on a one-time line
  }

  function distinctPlans(cart) {
    var set = new Set();
    (cart.items || []).forEach(function (it) {
      var p = planIdOf(it);
      if (p) set.add(p);
    });
    return set;
  }

  // Soft refresh: dispatch cart:refresh for the drawer and publish a cartUpdate payload
  // that carries rendered sections[] so the cart-items element can swap its own HTML in
  // place. Caller passes a cart already fetched with sections — if not, fetch one.
  function emitCartRefresh(cart) {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
    var publish = function (finalCart) {
      if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS && finalCart) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'yf-plan-unifier', cart: finalCart });
      }
    };
    if (cart && cart.sections) { publish(cart); return; }
    fetchCartWithSections().then(publish).catch(function (e) { LOG('soft refresh fetch error', e); publish(cart); });
  }

  // Cascade a target plan (string plan ID) or one-time ("" / null) to every cart line.
  // Skips lines already on the target. Tolerates per-line errors (e.g. product has no
  // matching selling plan — /cart/change returns 422; that line stays as-is).
  // Persist the in-flight cascade target so it can resume across navigations —
  // if the user closes the drawer or nav away mid-cascade, the next page load picks
  // up and finishes the job.
  // The marker also records respectExplicitOneTime, so a resumed cascade keeps
  // the same rules as the one it finishes (an add-triggered cascade must never
  // become a "set everyone" cascade on the next page).
  var CASCADE_TARGET_KEY = 'yf_cascade_target';
  function setCascadeMarker(target, respectExplicitOneTime) {
    try { sessionStorage.setItem(CASCADE_TARGET_KEY, JSON.stringify({ target: String(target || ''), respect: !!respectExplicitOneTime, ts: Date.now() })); } catch (e) {}
  }
  function clearCascadeMarker() {
    try { sessionStorage.removeItem(CASCADE_TARGET_KEY); } catch (e) {}
  }
  function readCascadeMarker() {
    try {
      var raw = sessionStorage.getItem(CASCADE_TARGET_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || typeof obj.target !== 'string') return null;
      if (Date.now() - (obj.ts || 0) > 5 * 60 * 1000) { clearCascadeMarker(); return null; }
      return { target: obj.target, respect: !!obj.respect };
    } catch (e) { return null; }
  }

  // True if a cascade to `target` still has work to do on this line. Lines
  // the cascade must leave alone (tag locks, explicit one-time when respected,
  // lines that already refused the plan) are never "unfinished".
  function lineNeedsTarget(it, target, locks, respectExplicitOneTime, refused) {
    if ((planIdOf(it) || '') === target) return false;
    if (respectExplicitOneTime && isExplicitOneTime(it)) return false;
    if (refused && refused[it.key]) return false;
    return !lockForbids(lockForItem(it, locks), target);
  }

  // opts.respectExplicitOneTime: true skips lines flagged as "user explicitly
  // chose one-time at add time". Used by the auto-cascade after add so we
  // don't override the shopper's deliberate choice. The picker tile click
  // path passes no opts (default false) so picking a tile is a "set everyone"
  // operation that overrides any previous explicit-one-time choices.
  function cascadeToAll(targetPlanId, opts) {
    if (cascading) { LOG('already cascading, skip'); return Promise.resolve(); }
    cascading = true;
    var target = targetPlanId ? String(targetPlanId) : '';
    var respectExplicitOneTime = !!(opts && opts.respectExplicitOneTime);
    LOG('cascade start →', target || '(one-time)', respectExplicitOneTime ? '(respect explicit one-time)' : '');
    setCascadeMarker(target, respectExplicitOneTime);
    showSpinner(target ? 'Updating your delivery' : 'Removing subscriptions\u2026');

    // Initial settle delay: Appstle fires its own /cart/change requests after ATB to update
    // subscription widgets; starting immediately guarantees a 409. Wait for the dust to
    // settle before our sequential pass begins.
    var locks = getLineLocks();
    // Lines whose product can't take the target (422) or that kept failing.
    // Excluded from the "finished" check so the resume marker isn't kept
    // alive for a line that will never move.
    var refused = {};

    return new Promise(function (res) { setTimeout(res, 600); })
      .then(fetchCart)
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return null;

        var keys = [];
        cart.items.forEach(function (it) {
          if (respectExplicitOneTime && isExplicitOneTime(it)) return;
          var current = planIdOf(it) || '';
          if (current === target) return;
          var lock = lockForItem(it, locks);
          if (lockForbids(lock, target)) {
            LOG('  skip line (tag-locked):', it.key, 'lock:', lock, 'target:', target || '(one-time)');
            return;
          }
          keys.push(it.key);
        });
        if (!keys.length) { LOG('nothing to cascade'); return cart; }

        // Sequential: Shopify uses optimistic locking on the cart; concurrent /cart/change
        // calls from the same session collide with 409 Conflict. One at a time is reliable.
        var chain = Promise.resolve();
        keys.forEach(function (key) {
          chain = chain.then(function () {
            return changeLine(key, target).catch(function (e) {
              refused[key] = true;
              LOG('  skip line (not eligible):', key, e && e.message);
            });
          });
        });
        return chain.then(fetchCartWithSections);
      })
      .then(function (finalCart) {
        // Only clear the resume marker if every line this cascade should move
        // is on the target. Tag-locked lines, respected explicit one-time lines
        // and refused lines are allowed exceptions and would otherwise keep the
        // marker alive (and a later resume would wrongly move them).
        var freshLocks = getLineLocks();
        var stillMismatched = finalCart && Array.isArray(finalCart.items) && finalCart.items.some(function (it) {
          return lineNeedsTarget(it, target, freshLocks, respectExplicitOneTime, refused);
        });
        if (!stillMismatched) clearCascadeMarker();
        else LOG('cascade finished with leftovers — marker kept for resume');
        emitCartRefresh(finalCart);
      })
      .catch(function (e) { LOG('cascade error', e); })
      .then(function () { cascading = false; hideSpinner(); runQueuedPick(); });
  }

  // A delivery-frequency pick made while a cascade is running (a quick
  // second click: Quarterly, then 3rd Saturday) was dropped by the
  // "already cascading" guard. Keep the latest pick and run it once the
  // running cascade ends.
  var queuedPick = null;
  function runQueuedPick() {
    if (queuedPick === null || cascading) return;
    var next = queuedPick;
    queuedPick = null;
    LOG('queued pick → cascade', next || '(one-time)');
    cascadeToAll(next);
  }

  function setLineTo(lineKey, planId) {
    if (cascading) return Promise.resolve();
    cascading = true;
    return changeLine(lineKey, planId)
      .then(fetchCartWithSections)
      .then(function (cart) { emitCartRefresh(cart); })
      .catch(function (e) { LOG('setLineTo error', e); })
      .then(function () { cascading = false; });
  }

  window.YFCartPlan = { setAllLinesTo: cascadeToAll, setLineTo: setLineTo };

  // Filter our drawer per-line <select data-yf-line-plan> — hide options whose selling-plan
  // group doesn't match the saved zone's Appstle group. Works on any page (the drawer is
  // rendered site-wide), independent of the cart-page postcode gate.
  function getSavedGroupGid() {
    try {
      var raw = localStorage.getItem('yf_delivery_selection');
      if (!raw) return null;
      var sel = JSON.parse(raw);
      return sel && sel.appstleGroupGid ? String(sel.appstleGroupGid) : null;
    } catch (e) { return null; }
  }

  function filterDrawerSelects() {
    var groupGid = getSavedGroupGid();
    if (!groupGid) return;
    var numeric = groupGid.replace(/.*\//, '');

    // Filter tile-grid plan pickers: hide tiles whose data-group-gid doesn't match the zone.
    // Tiles with no data-yf-plan value (e.g. "One-time purchase") always stay visible.
    document.querySelectorAll('[data-yf-combined-plan]').forEach(function (wrap) {
      wrap.querySelectorAll('.yf-combined-plan__tile').forEach(function (tile) {
        var planId = tile.getAttribute('data-yf-plan') || '';
        if (!planId) { tile.hidden = false; return; }
        var optGid = tile.getAttribute('data-group-gid') || '';
        var optNum = optGid.replace(/.*\//, '');
        var match = optGid === groupGid || (numeric && optNum === numeric);
        tile.hidden = !match;
      });
    });

    // Legacy: filter any lingering <select> pickers the same way.
    document.querySelectorAll('select[data-yf-combined-plan], select[data-yf-line-plan]').forEach(function (select) {
      var firstVisible = null;
      var currentVisible = false;
      Array.from(select.options).forEach(function (opt) {
        if (!opt.value) {
          opt.hidden = false; opt.disabled = false; opt.style.display = '';
          if (opt.selected) currentVisible = true;
          return;
        }
        var optGid = opt.getAttribute('data-group-gid') || '';
        var optNum = optGid.replace(/.*\//, '');
        var match = optGid === groupGid || (numeric && optNum === numeric);
        opt.hidden = !match;
        opt.disabled = !match;
        opt.style.display = match ? '' : 'none';
        if (match && !firstVisible) firstVisible = opt;
        if (match && opt.selected) currentVisible = true;
      });
      if (!currentVisible && firstVisible) firstVisible.selected = true;
    });
  }

  // Tile click → cascade. Also updates aria-checked/is-selected state locally so the UI
  // reflects the choice immediately; the section re-render will overwrite after cascade.
  document.addEventListener('click', function (e) {
    var tile = e.target && e.target.closest && e.target.closest('.yf-combined-plan__tile');
    if (!tile) return;
    e.preventDefault();
    var planId = tile.getAttribute('data-yf-plan') || '';
    var wrap = tile.closest('[data-yf-combined-plan]');
    if (wrap) {
      wrap.querySelectorAll('.yf-combined-plan__tile').forEach(function (t) {
        t.classList.toggle('is-selected', t === tile);
        t.setAttribute('aria-checked', t === tile ? 'true' : 'false');
      });
    }
    if (cascading) {
      LOG('tile click while cascading → queued', planId || '(one-time)');
      queuedPick = planId;
      return;
    }
    LOG('tile click → cascade', planId || '(one-time)');
    cascadeToAll(planId);
  });

  // "Save 10% by subscribing" banner click -> scroll to the delivery-frequency
  // picker and pulse it so the shopper's eye lands on the right control.
  // The picker itself handles the actual cascade when they pick a tile.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-yf-subscribe-prompt]');
    if (!btn) return;
    e.preventDefault();
    // Prefer a picker in the same scroll container as the banner (drawer
    // vs main cart) so the scroll feels right.
    var scope = btn.closest('cart-drawer') || btn.closest('main-cart') || document;
    var picker = scope.querySelector('[data-yf-combined-plan]')
              || document.querySelector('[data-yf-combined-plan]');
    if (!picker) return;
    try { picker.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e2) {}
    picker.classList.remove('yf-combined-plan--yf-flash');
    // Force a reflow so the animation re-runs even if classlist already had it.
    void picker.offsetWidth;
    picker.classList.add('yf-combined-plan--yf-flash');
    setTimeout(function () { picker.classList.remove('yf-combined-plan--yf-flash'); }, 1500);
  });

  function scheduleDrawerFilter() {
    [0, 150, 500, 1200].forEach(function (d) { setTimeout(filterDrawerSelects, d); });
  }

  document.addEventListener('cart:refresh', scheduleDrawerFilter);
  document.addEventListener('DOMContentLoaded', scheduleDrawerFilter);
  // A newly chosen suburb re-filters the tiles (yf-suburb-required.js un-hides
  // them first; this narrows them to the new zone).
  document.addEventListener('yf:zone-changed', scheduleDrawerFilter);

  // ---------- Suburb change: fit the cart to the new zone ------------------
  // Each zone is its own Appstle selling-plan group, so after a suburb change
  // the cart's subscription lines are still on the old zone's plan (wrong
  // delivery run, and no frequency tile looks selected). Move them to the new
  // zone's plan with the same frequency (monthly stays monthly). If the new
  // zone doesn't offer that frequency, use its default plan. One-time lines
  // keep their purchase type.
  // A line whose product isn't sold in the new zone (its product isn't in the
  // zone's group) can't be delivered there, so it is removed, subscription or
  // one-time, and the shopper is told which. Products with no plan groups at
  // all are available everywhere and stay.

  // Frequency key such as "1MONTH". Appstle starts every plan option value
  // with the interval ("2MONTH2MONTHMIN_CYCLES=...EVERY 2ND MONTH (...)");
  // the plan name is a fallback.
  function planFrequency(plan) {
    if (!plan) return '';
    var opts = plan.options || [];
    for (var i = 0; i < opts.length; i++) {
      var m = /^(\d+)\s*(DAY|WEEK|MONTH|YEAR)/i.exec(String((opts[i] && opts[i].value) || ''));
      if (m) return m[1] + m[2].toUpperCase();
    }
    var n = String(plan.name || '').toUpperCase();
    if (/QUARTERLY/.test(n)) return '3MONTH';
    if (/2ND MONTH|BI-?MONTHLY/.test(n)) return '2MONTH';
    if (/MONTHLY/.test(n)) return '1MONTH';
    if (/FORTNIGHT/.test(n)) return '2WEEK';
    if (/WEEKLY/.test(n)) return '1WEEK';
    return '';
  }

  function gidSuffix(gid) { return String(gid || '').split('/').pop().toLowerCase(); }

  function fetchProductJson(handle) {
    var root = (window.theme && theme.routes && theme.routes.root_url) || '/';
    if (root.slice(-1) !== '/') root += '/';
    return nativeFetch()(root + 'products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('product HTTP ' + r.status);
      return r.json();
    });
  }

  // Remove a line (quantity 0). Retries a 409 like changeLine.
  function removeLine(lineKey, attempt) {
    attempt = attempt || 0;
    return nativeFetch()(CART_CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    }).then(function (r) {
      if (r.status === 409 && attempt < 3) {
        return new Promise(function (res) { setTimeout(res, 200 + 150 * attempt); })
          .then(function () { return removeLine(lineKey, attempt + 1); });
      }
      if (!r.ok) throw new Error('cart/change ' + r.status);
      return r.json();
    });
  }

  // Short notice listing the lines removed after a suburb change.
  function showRemovedNotice(titles, place) {
    var id = 'yf-zone-removed-notice';
    var old = document.getElementById(id);
    if (old) old.remove();
    var box = document.createElement('div');
    box.id = id;
    box.setAttribute('role', 'status');
    box.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483001;' +
      'max-width:min(92vw,520px);background:#171717;color:#fff;padding:14px 44px 14px 18px;border-radius:12px;' +
      'box-shadow:0 8px 24px rgba(0,0,0,.25);font-size:.95rem;line-height:1.4';
    var text = document.createElement('p');
    text.style.margin = '0';
    text.textContent = 'We don’t deliver ' + titles.join(', ') + ' to ' + place +
      ', so ' + (titles.length > 1 ? 'they have' : 'it has') + ' been removed from your cart.';
    var close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';
    close.style.cssText = 'position:absolute;top:6px;right:8px;background:none;border:0;color:#fff;font-size:1.4rem;line-height:1;cursor:pointer;padding:4px 8px';
    close.addEventListener('click', function () { box.remove(); });
    box.appendChild(text);
    box.appendChild(close);
    document.body.appendChild(box);
    setTimeout(function () { if (box.isConnected) box.remove(); }, 12000);
  }

  function titleCase(str) {
    return String(str || '').toLowerCase().replace(/(^|[\s-])\S/g, function (c) { return c.toUpperCase(); });
  }

  var zoneReplanTries = 0;
  function replanForZone() {
    var sel;
    try { sel = JSON.parse(localStorage.getItem('yf_delivery_selection') || 'null'); } catch (e) { sel = null; }
    if (!sel || !sel.appstleGroupGid) return; // suburb cleared: nothing to move to
    if (cascading) {
      if (zoneReplanTries++ < 10) setTimeout(replanForZone, 800);
      return;
    }
    zoneReplanTries = 0;
    var wanted = gidSuffix(sel.appstleGroupGid);
    var defaultPlan = gidSuffix(sel.defaultPlanGid);

    fetchCart().then(function (cart) {
      var items = (cart && cart.items) || [];
      if (!items.length) return;
      var handles = [];
      items.forEach(function (it) { if (it.handle && handles.indexOf(it.handle) < 0) handles.push(it.handle); });

      return Promise.all(handles.map(function (h) {
        return fetchProductJson(h).then(function (p) { return p; }, function () { return null; });
      })).then(function (list) {
        var products = {};
        handles.forEach(function (h, i) { products[h] = list[i]; });

        // Sold in the new zone? Unknown products and products with no plan
        // groups count as yes (never remove on missing data).
        function inZone(handle) {
          var p = products[handle];
          var groups = (p && p.selling_plan_groups) || [];
          if (!p || !groups.length) return true;
          return groups.some(function (g) { return gidSuffix(g.id) === wanted; });
        }

        var removals = items.filter(function (it) { return !inZone(it.handle); });

        // The new zone's group (shared across products, so any member will do).
        var group = null;
        handles.forEach(function (h) {
          if (group || !products[h]) return;
          group = (products[h].selling_plan_groups || []).filter(function (g) { return gidSuffix(g.id) === wanted; })[0] || null;
        });

        var moves = [];
        var plans = (group && group.selling_plans) || [];
        if (plans.length) {
          var inGroup = {};
          plans.forEach(function (sp) { inGroup[String(sp.id)] = sp; });
          items.forEach(function (it) {
            if (!planIdOf(it) || !inZone(it.handle)) return;
            if (inGroup[planIdOf(it)]) return; // already on this zone's plan
            var freq = planFrequency(it.selling_plan_allocation.selling_plan);
            var same = freq ? plans.filter(function (sp) { return planFrequency(sp) === freq; }) : [];
            var pick = same.filter(function (sp) { return String(sp.id) === defaultPlan; })[0] ||
              same[0] || inGroup[defaultPlan] || plans[0];
            moves.push({ key: it.key, plan: String(pick.id) });
          });
        }
        if (!removals.length && !moves.length) return;

        LOG('zone replan → remove', removals.map(function (it) { return it.key; }), 'move', moves);
        cascading = true;
        showSpinner('Updating your delivery');
        var removedTitles = [];
        var chain = Promise.resolve();
        removals.forEach(function (it) {
          chain = chain.then(function () {
            return removeLine(it.key).then(function () {
              if (removedTitles.indexOf(it.product_title) < 0) removedTitles.push(it.product_title);
            }, function (e) { LOG('  zone replan remove failed:', it.key, e && e.message); });
          });
        });
        moves.forEach(function (m) {
          chain = chain.then(function () {
            return changeLine(m.key, m.plan).catch(function (e) {
              LOG('  zone replan skip line:', m.key, e && e.message);
            });
          });
        });
        return chain
          .then(fetchCartWithSections)
          .then(function (finalCart) { emitCartRefresh(finalCart); })
          .catch(function (e) { LOG('zone replan error', e); })
          .then(function () {
            cascading = false;
            hideSpinner();
            if (removedTitles.length) showRemovedNotice(removedTitles, titleCase(sel.locality) || 'your suburb');
            runQueuedPick();
          });
      });
    }).catch(function (e) { LOG('zone replan fetch error', e); });
  }

  var zoneReplanTimer = null;
  document.addEventListener('yf:zone-changed', function () {
    clearTimeout(zoneReplanTimer);
    zoneReplanTimer = setTimeout(replanForZone, 300);
  });
  // Cart icon click → prevent navigation, open the drawer directly. This is a hard
  // override for the header cart icon: some themes/ancestor handlers re-trigger
  // navigation even after the drawer's own onButtonClick preventDefault, causing a
  // full-page reload to /cart after the drawer opens briefly.
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a.cart-drawer-button, a[aria-controls="CartDrawer"]');
    if (!a) {
      if (e.target && e.target.closest && e.target.closest('[data-cart-drawer-toggle], cart-drawer-button, a[href$="/cart"]')) {
        scheduleDrawerFilter();
      }
      return;
    }
    // Only intercept if the drawer exists on the page (cart_type === 'drawer').
    var drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    e.preventDefault();
    e.stopPropagation();
    scheduleDrawerFilter();
    if (typeof drawer.show === 'function') {
      try { drawer.show(); } catch (err) { LOG('drawer.show error', err); }
    }
  }, true);

  // Hook the filter into cartUpdate pubsub too — item removal (source: cart-items)
  // and quantity changes trigger section re-renders that recreate our tiles from
  // unfiltered Liquid, so we need to re-apply the zone filter each time.
  function attachFilterToCartUpdate() {
    if (!window.theme || !theme.pubsub || !theme.pubsub.PUB_SUB_EVENTS) {
      setTimeout(attachFilterToCartUpdate, 200);
      return;
    }
    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (payload) {
      if (payload && payload.source === 'yf-plan-unifier') return; // our own events
      scheduleDrawerFilter();
    });
  }
  attachFilterToCartUpdate();

  function evaluateAndCascade(cart, variantId, reason) {
    if (cascading) return;
    if (!cart || !Array.isArray(cart.items)) return;
    var locks = getLineLocks();
    LOG(reason, '— added variant:', variantId, 'cart lines:',
      cart.items.map(function (it) {
        return {
          variant: it.variant_id,
          title: it.product_title,
          plan: planIdOf(it) || '(one-time)',
          lock: lockForItem(it, locks),
          marker: isExplicitOneTime(it) ? 'explicit-one-time' : ''
        };
      })
    );

    // If the just-added line was flagged as explicit one-time (shopper picked
    // the One-time radio on the PDP/quick-view, not a quick-add), don't drag
    // it onto the cart's existing subscription plan. The whole cart can have
    // a single sub plan plus zero-or-more one-time lines coexisting.
    var addedLine = cart.items.filter(function (it) { return String(it.variant_id) === String(variantId); })[0];
    if (addedLine && isExplicitOneTime(addedLine) && !planIdOf(addedLine)) {
      LOG(reason, '— added line is explicit one-time; skipping cascade');
      return;
    }

    var winning = planForVariant(cart, variantId);
    if (!winning) {
      // Quick-add / complementary-add doesn't carry a selling_plan AND has no
      // explicit-one-time marker (the explicit-one-time check ran earlier).
      // Inherit cart's existing sub plan so cross-sells join the subscription
      // — UNLESS the freshly-added line is tagged one-time-only via the
      // product-tag lock, in which case it stays one-time forever (the tag
      // says the product type itself isn't subscribable, irrespective of
      // shopper choice).
      var addedLock = addedLine ? lockForItem(addedLine, locks) : null;
      if (addedLock && addedLock.oneTimeOnly) {
        LOG(reason, '— added item is one-time-only (tag lock), skipping subscription inheritance');
        return;
      }
      for (var i = 0; i < cart.items.length; i++) {
        var p = planIdOf(cart.items[i]);
        if (p) { winning = p; break; }
      }
      if (!winning) { LOG(reason, '— added item is one-time and no existing subscription in cart'); return; }
      LOG(reason, '— added as one-time; inheriting cart\'s subscription →', winning);
    }
    // Cascade target acquired. Now figure out which lines actually need to
    // change. Skip three categories of lines:
    //   1. Already on the target plan (no change needed).
    //   2. Shopper-marked explicit-one-time (don't override their choice).
    //   3. Tag-locked one-time-only / subscription-only (product type lock).
    // The picker tile click path overrides #2 explicitly via cascadeToAll
    // without respectExplicitOneTime; this auto-cascade after-add path
    // honours all three.
    var mismatched = cart.items.filter(function (it) {
      if ((planIdOf(it) || '') === String(winning)) return false;
      if (isExplicitOneTime(it)) return false;
      return !lockForbids(lockForItem(it, locks), String(winning));
    });
    LOG(reason, '— winning:', winning, 'mismatched:', mismatched.length, '/', cart.items.length);
    if (!mismatched.length) return;
    cascadeToAll(winning, { respectExplicitOneTime: true });
  }

  // Subscribe to cartUpdate — any source except our own triggers evaluation.
  function attach() {
    if (!window.theme || !theme.pubsub || !theme.pubsub.PUB_SUB_EVENTS) {
      setTimeout(attach, 200);
      return;
    }
    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (payload) {
      if (!payload) return;
      if (payload.source === 'yf-plan-unifier') return;

      // Safety-net: ATB spinner was shown on form submit; if this cartUpdate has no
      // variantId (meaning it's not a product-form event), clear the timer & hide.
      if (!payload.productVariantId) {
        LOG('cartUpdate received, source:', payload.source, '(no variantId, skip)');
        clearAtbSpinner();
        return;
      }
      LOG('cartUpdate received, source:', payload.source, 'variant:', payload.productVariantId);
      // AJAX ATB handled here — clear the stash so the next page load doesn't re-cascade.
      try { sessionStorage.removeItem(ATB_STASH_KEY); } catch (e) {}

      // evaluateAndCascade may or may not start a cascade. If it doesn't start one,
      // hide the ATB spinner now; if it does, the cascade's own show/hide owns it.
      var wasCascading = cascading;
      evaluateAndCascade(payload.cart, payload.productVariantId, 'cartUpdate[' + (payload.source || '?') + ']');
      if (!cascading && !wasCascading) clearAtbSpinner();
      else if (atbSpinnerTimer) { clearTimeout(atbSpinnerTimer); atbSpinnerTimer = null; }
    });
    LOG('cartUpdate listener attached, events:', Object.keys(theme.pubsub.PUB_SUB_EVENTS));
  }
  attach();

  // --- Full-page ATB bridge ---------------------------------------------------------------
  // When settings.cart_type === 'page', the product form is a regular form POST to
  // /cart/add and the browser navigates straight to /cart — no AJAX, no pubsub event.
  // Capture the intent here (at submit) and replay it on the next page load.

  var ATB_STASH_KEY = 'yf_last_atb';

  // Show the rotating-cow spinner as soon as an ATB is submitted, keep it up until the
  // cascade finishes (if one fires) or a short timeout elapses (if it doesn't).
  var atbSpinnerTimer = null;
  function showAtbSpinner() {
    showSpinner(null, { rotate: true });
    if (atbSpinnerTimer) clearTimeout(atbSpinnerTimer);
    // Safety net: if no cartUpdate event arrives (e.g. third-party intercept or error),
    // hide the spinner after 6s so we don't leave a permanent overlay.
    atbSpinnerTimer = setTimeout(function () {
      if (!cascading) hideSpinner();
    }, 6000);
  }
  function clearAtbSpinner() {
    if (atbSpinnerTimer) { clearTimeout(atbSpinnerTimer); atbSpinnerTimer = null; }
    // If cascade started, it owns the spinner — don't hide here.
    if (!cascading) hideSpinner();
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    showAtbSpinner();

    var fd, variantId, sellingPlan;
    try {
      fd = new FormData(form);
      variantId = fd.get('id');
      sellingPlan = fd.get('selling_plan');
    } catch (err) { LOG('formdata error', err); return; }
    if (!variantId) return;

    // Explicit-one-time marker: form has a selling_plan input (so the shopper
    // was offered a choice) but submitted with empty value (they chose
    // one-time). Inject a properties[_yf_explicit_onetime]=1 hidden input so
    // the line item carries the marker into the cart, where evaluateAndCascade
    // can read it and skip auto-converting the line onto an existing
    // subscription. Quick-add/collection-grid forms have no selling_plan
    // input at all, so this branch doesn't fire and they keep the legacy
    // "inherit cart's subscription" behavior.
    var hasPlanPicker = !!form.querySelector('[name="selling_plan"]');
    var explicitOneTime = hasPlanPicker && !sellingPlan;
    if (explicitOneTime) {
      var existingMarker = form.querySelector('input[name="properties[_yf_explicit_onetime]"]');
      if (!existingMarker) {
        var marker = document.createElement('input');
        marker.type = 'hidden';
        marker.name = 'properties[_yf_explicit_onetime]';
        marker.value = '1';
        form.appendChild(marker);
        // Keep FormData in sync with the form's new state so the rest of the
        // listener (and any other capture-phase listeners) see it too.
        fd.append('properties[_yf_explicit_onetime]', '1');
      }
      LOG('explicit one-time detected on form, marker injected');
    } else if (hasPlanPicker) {
      // Shopper picked a sub plan -- ensure no stale marker from a previous
      // one-time selection lingers on the same form (e.g. PDP that was
      // re-opened without reload).
      var stale = form.querySelector('input[name="properties[_yf_explicit_onetime]"]');
      if (stale) stale.parentNode.removeChild(stale);
    }

    // On the cart page, theme.js deliberately lets /cart/add forms post natively (full
    // page navigation) instead of AJAX. That triggers the browser's own spinner and
    // wipes our in-page state. Take over: preventDefault, do an AJAX POST ourselves,
    // then publish cartUpdate so the cascade + section re-render path handles the rest.
    var onCartPage = document.body.classList.contains('template-cart');
    if (onCartPage) {
      e.preventDefault();
      e.stopPropagation();

      // Clean the form body so /cart/add gets only what it expects. FormData is the
      // easiest fetch body shape and preserves all hidden inputs the theme may add.
      var cartAddUrl = (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js';
      nativeFetch()(cartAddUrl, {
        method: 'POST',
        headers: { 'Accept': 'application/javascript' },
        body: fd
      }).then(function (r) {
        return r.ok ? r.json() : r.json().then(function (j) {
          var err = new Error('cart/add ' + r.status); err.status = r.status; err.body = j; throw err;
        });
      }).then(function (/* addedLine */) {
        // Fetch fresh cart (w/ sections) so the downstream cartUpdate has everything
        // cart-items needs to swap HTML in place.
        return fetchCartWithSections().then(function (cart) {
          if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: String(variantId),
              cart: cart
            });
          }
        });
      }).catch(function (err) {
        LOG('cart-page AJAX ATB error', err);
        clearAtbSpinner();
      });
      return;
    }

    // Drawer / other pages — theme.js handles the AJAX. We just stash for continuity
    // (full-redirect cart_type fallback) and let it run.
    try {
      sessionStorage.setItem(ATB_STASH_KEY, JSON.stringify({
        variantId: String(variantId),
        sellingPlan: sellingPlan ? String(sellingPlan) : '',
        ts: Date.now()
      }));
      LOG('ATB stashed →', variantId, sellingPlan || '(one-time)');
    } catch (err) { LOG('stash error', err); }
  }, true);

  function consumeAtbStash() {
    var raw;
    try { raw = sessionStorage.getItem(ATB_STASH_KEY); } catch (e) { return null; }
    if (!raw) return null;
    try { sessionStorage.removeItem(ATB_STASH_KEY); } catch (e) {}
    try {
      var obj = JSON.parse(raw);
      if (!obj || !obj.variantId) return null;
      // Stale entries (>60s old) — ignore to avoid acting on ancient state.
      if (Date.now() - (obj.ts || 0) > 60000) return null;
      return obj;
    } catch (e) { return null; }
  }

  function processAtbStash() {
    var stash = consumeAtbStash();
    if (!stash) return;
    LOG('consuming ATB stash →', stash);
    // Always hand off to evaluateAndCascade — the no-plan branch there finds an
    // existing subscription in the cart and cascades the new one-time item onto it,
    // which is exactly the behaviour complementary quick-adds need.
    fetchCart().then(function (cart) {
      evaluateAndCascade(cart, stash.variantId, 'atb-stash[' + (stash.sellingPlan || 'one-time') + ']');
    }).catch(function (e) { LOG('stash cart fetch error', e); });
  }

  // Resume an interrupted cascade: if a previous cascade left the cart mismatched
  // (user closed the drawer, nav away, tab lost focus, etc.), finish the job now.
  function resumeInterruptedCascade() {
    var marker = readCascadeMarker();
    if (marker === null) return;
    if (cascading) return;
    var target = marker.target;
    fetchCart().then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) { clearCascadeMarker(); return; }
      var locks = getLineLocks();
      var mismatched = cart.items.some(function (it) {
        return lineNeedsTarget(it, target, locks, marker.respect, null);
      });
      if (!mismatched) { clearCascadeMarker(); return; }
      LOG('resuming interrupted cascade → target', target || '(one-time)', marker.respect ? '(respect explicit one-time)' : '');
      cascadeToAll(target, { respectExplicitOneTime: marker.respect });
    }).catch(function (e) { LOG('resume fetch error', e); });
  }

  function onReady() {
    setTimeout(processAtbStash, 300);
    setTimeout(resumeInterruptedCascade, 800);
  }
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    onReady();
  } else {
    document.addEventListener('DOMContentLoaded', onReady);
  }

  // Delegate: any selling-plan <select> change cascades to every line in the cart.
  // Empty value ("One-time purchase") also cascades — removes the plan from every line.
  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || el.tagName !== 'SELECT') return;

    var name = (el.name || '').toLowerCase();
    var cls = (typeof el.className === 'string' ? el.className : '').toLowerCase();
    var isYfCombined = el.hasAttribute('data-yf-combined-plan');
    var isYfLine = el.hasAttribute('data-yf-line-plan');
    var isAppstleCombined = cls.indexOf('appstle-combined-cart-widget-select') !== -1;
    var isAppstlePerLine = cls.indexOf('appstle_selling_plan_cart') !== -1;
    var isNamedPlan = name === 'selling_plan';

    if (!isYfCombined && !isYfLine && !isAppstleCombined && !isAppstlePerLine && !isNamedPlan) return;

    var val = el.value;
    // Valid plan ID → cascade it. Empty ("One-time") → cascade empty.
    // Appstle's special "appstle_unsubscribe" option means one-time → treat as empty.
    var target = (val && /^\d{4,}$/.test(val)) ? val : '';
    LOG('select change → cascade', target || '(one-time)', '(from', cls || name || 'yf-drawer', ')');
    cascadeToAll(target);
  });

  // --- Shipping-calculator autofill ------------------------------------------------------
  // The cart drawer and cart page both render Shopify's built-in shipping-rate estimator
  // with Country / Province / Zip fields. Since every YF customer delivers to Australia
  // and we already captured their postcode + state in the postcode gate, there's no reason
  // to ask again — prefill all three and let them just hit the button.

  var AU_STATE_MAP = {
    NSW: 'New South Wales',
    VIC: 'Victoria',
    QLD: 'Queensland',
    WA: 'Western Australia',
    SA: 'South Australia',
    TAS: 'Tasmania',
    ACT: 'Australian Capital Territory',
    NT: 'Northern Territory'
  };

  function getShippingAutofillValues() {
    try {
      var raw = localStorage.getItem('yf_delivery_selection');
      if (!raw) return null;
      var sel = JSON.parse(raw);
      if (!sel) return null;
      var stateAbbr = (sel.state || '').toString().toUpperCase().trim();
      return {
        country: 'Australia',
        province: AU_STATE_MAP[stateAbbr] || '',
        zip: (sel.pcode || '').toString().trim()
      };
    } catch (e) { return null; }
  }

  function setSelectValue(select, value) {
    if (!select || value == null) return false;
    var options = Array.from(select.options);
    var target = options.find(function (o) { return o.value === value; })
              || options.find(function (o) { return (o.text || '').trim() === value; });
    if (!target) return false;
    if (select.value === target.value) return true;
    select.value = target.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function autofillShippingForm(form) {
    if (!form || form.__yfAutofilled) return;
    var vals = getShippingAutofillValues();
    if (!vals) return;

    var country = form.querySelector('select[name="address[country]"]');
    var province = form.querySelector('select[name="address[province]"]');
    var zip = form.querySelector('input[name="address[zip]"]');

    // Country — triggering change rebuilds the province options.
    if (country && setSelectValue(country, vals.country)) {
      // Province dropdown is rebuilt async by the <country-province> custom element.
      // Retry the province set on microtask + short timeouts until the option appears.
      var attempts = 0;
      (function trySetProvince() {
        if (!province || !vals.province) return;
        if (setSelectValue(province, vals.province)) return;
        if (attempts++ < 10) setTimeout(trySetProvince, 60);
      })();
    }
    // Always overwrite zip when autofill runs. The function only runs when
    // the form hasn't been autofilled yet (per __yfAutofilled flag) or the
    // flag was cleared because the shopper changed suburb. In both cases
    // any value already in the field is stale (from the previous suburb)
    // and should be replaced with the current selection.
    if (zip && vals.zip) zip.value = vals.zip;

    form.__yfAutofilled = true;
    LOG('shipping calc autofilled ->', vals);
  }

  function scanAndAutofillShippingForms() {
    document.querySelectorAll('form[is="shipping-calculator"]').forEach(autofillShippingForm);
  }

  // On page load, autofill any statically rendered forms (main cart page).
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(scanAndAutofillShippingForms, 200);
  } else {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(scanAndAutofillShippingForms, 200); });
  }
  // On cart re-renders, re-scan (the form may be swapped in fresh from section HTML).
  document.addEventListener('cart:refresh', function () { setTimeout(scanAndAutofillShippingForms, 200); });

  // When the shopper changes their suburb, clear the per-form "already filled"
  // flag and re-run autofill so country/state/zip pick up the new selection.
  // Without this, the form keeps showing the previous suburb's postcode.
  document.addEventListener('yf:zone-changed', function () {
    document.querySelectorAll('form[is="shipping-calculator"]').forEach(function (form) {
      form.__yfAutofilled = false;
    });
    setTimeout(scanAndAutofillShippingForms, 50);
  });


  // Drawer's shipping form is injected via section-rendering API when the "Shipping"
  // button is clicked. Observe drawer body for the injected form and autofill when it
  // appears.
  var shippingObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (!m.addedNodes || !m.addedNodes.length) continue;
      for (var j = 0; j < m.addedNodes.length; j++) {
        var n = m.addedNodes[j];
        if (n.nodeType !== 1) continue;
        var forms = n.matches && n.matches('form[is="shipping-calculator"]')
          ? [n]
          : (n.querySelectorAll ? Array.from(n.querySelectorAll('form[is="shipping-calculator"]')) : []);
        forms.forEach(autofillShippingForm);
      }
    }
  });
  if (document.body) shippingObserver.observe(document.body, { childList: true, subtree: true });
  else document.addEventListener('DOMContentLoaded', function () {
    shippingObserver.observe(document.body, { childList: true, subtree: true });
  });

  LOG('ready');
})();
